import "./style.scss";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Avatar, debounce, IconButton } from "@mui/material";
import variables from "assets/styles/_variables.module.scss";
import BlockCards from "components/shared/BlockCards";
import ElementDetails from "components/shared/ElementDetails";
import FilterInput from "components/shared/FilterInput";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import { Spinner } from "components/shared/Spinner";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { formatNumber, getInitials } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAnalyseHistory, fetchAnalyseHistoryDetails, fetchContributions } from "store/contributionStore";
import { useAppDispatch, useAppSelector } from "store/store";

const AnalysePage = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const {
        analyseHistory,
        analyseHistoryStatus,
        analyseHistoryDetails,
        analyseHistoryDetailsStatus,
        contributionList,
    } = useAppSelector((state) => state.contribution);
    const [currentRowId, setCurrentRowId] = useState();
    const [currentRowIndex, setCurrentRowIndex] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const detailsText = t("Management.Details");
    const [dateFilters, setDateFilters] = useState({
        startQuarter: 1,
        startYear: 2023,
        endQuarter: 4,
        endYear: 2023,
    });

    const { query, handleQuery, objectCount, setOffsetPagination, setExtraParams } = useAPIPagination({
        type: "analyseHistory",
        defaultLimitPagination: 15,
    });

    const totalBeneficiar = useMemo(
        () => objectCount?.reduce((curr: number, acc: any) => acc.countBeneficiariesPerQuarter + curr, 0),
        [objectCount],
    );
    const totalGrossDuty = useMemo(
        () => objectCount?.reduce((curr: number, acc: any) => acc.totalGrossDutyPerQuarter + curr, 0),
        [objectCount],
    );
    const detailsTotalGrossDuty = useMemo(
        () => analyseHistoryDetails?.reduce((curr: number, acc: any) => acc.totalGrossDuty + curr, 0),
        [analyseHistoryDetails],
    );
    const analyseHistoryMemoized = useMemo(
        () => analyseHistory?.map((analyse) => ({ ...analyse, details: detailsText })),
        [analyseHistory],
    );

    const firstDeclaration = useMemo(() => {
        if (!analyseHistoryDetails) return;
        const contribList = analyseHistoryDetails;
        // takes the min out of all years
        const year = Math.min(...contribList.map((item: any) => item.year));
        // takes the min out of all quarters that are not 0 and are equals to startYear
        const quarter = Math.min(
            ...contribList
                .filter((jtem: any) => jtem.quarter != 0 && jtem.year == year)
                .map((item: any) => item.quarter),
        );
        return { year, quarter };
    }, [analyseHistoryDetails]);

    const loadMoreHistory = useCallback(
        debounce(() => setOffsetPagination(analyseHistory?.length ?? 0), 400),
        [analyseHistory],
    );

    const cards = [
        { unit: totalBeneficiar, label: t("Beneficiars.Beneficiars") },
        {
            unit: formatNumber(totalGrossDuty / 100, {
                always2Digit: true,
                isCurrency: true,
            }),
            label: t("Analyse.GrossRevenue"),
        },
    ];

    const columns: LastElementTableColumns[] = [
        {
            id: "firstname",
            avatar: true,
            label: t("Beneficiars.Beneficiars"),
            align: "left",
            format: (value, id, row) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Avatar
                        variant="circular"
                        src={"#"}
                        sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: variables["beneficiary-avatar-background"],
                            color: variables["beneficiary-avatar-letter"],
                            fontSize: 8,
                        }}
                        imgProps={{ sx: { objectFit: "cover" } }}
                    >
                        {getInitials(`${row.firstname} ${row.lastname}`)}
                    </Avatar>
                    {`${row.firstname} ${row.lastname}`}
                </div>
            ),
        },
        {
            id: "totalGrossDuty",
            label: t("Analyse.GrossRevenue"),
            align: "left",
            format: (value, id, row) => formatNumber(value / 100, { always2Digit: true, isCurrency: true }),
        },
        {
            id: "details",
            label: "",
            align: "right",
            format: (value, id, row) => <div className="analyse-page-table-details">{value}</div>,
        },
    ];

    const detailsColumns: LastElementTableColumns[] = [
        {
            id: "quarter",
            label: t("Analyse.Period"),
            align: "left",
            format: (value, id, row) => `T${value} ${row.year}`,
        },
        {
            id: "totalGrossDuty",
            label: t("Analyse.GrossRevenue"),
            align: "right",
            format: (value, id, row) => formatNumber(value / 100, { always2Digit: true, isCurrency: true }),
        },
    ];

    const handleCurrentElement = (e: number) => {
        setCurrentRowId(analyseHistoryMemoized[e]);
        setCurrentRowIndex(e);
    };

    const handleOpenDrawer = (row: string) => {
        const rowIndex = analyseHistoryMemoized.findIndex((an) => an.unique_identifier == row);
        setCurrentRowId(analyseHistoryMemoized[rowIndex]);
        setCurrentRowIndex(rowIndex);
        setIsDrawerOpen(true);
    };

    useEffect(() => {
        if (!currentRowId) return;
        dispatch(
            fetchAnalyseHistoryDetails({
                uuid: (currentRowId as any)?.unique_identifier,
                dateFilters,
            }),
        );
    }, [currentRowId]);

    useEffect(() => {
        setExtraParams(dateFilters);
        dispatch(fetchContributions()).then((resp) => {
            const contribList = resp.payload;
            // takes the min out of all years
            const startYear = Math.min(...contribList.map((item: any) => item.year));
            // takes the min out of all quarters that are not 0 and are equals to startYear
            const startQuarter = Math.min(
                ...contribList
                    .filter((jtem: any) => jtem.quarter != 0 && jtem.year == startYear)
                    .map((item: any) => item.quarter),
            );
            const startFilter = { startYear, startQuarter };
            const filter = {
                ...startFilter,
                endQuarter: contribList.at(-1)!.quarter,
                endYear: contribList.at(-1)!.year,
            };
            setDateFilters(filter);
            setExtraParams(filter);
        });
    }, []);

    useEffect(() => {
        setExtraParams(dateFilters);
    }, [dateFilters]);

    return (
        <div className="analyse-page">
            <ElementDetails
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                elements={analyseHistoryMemoized}
                setElementIndex={(e: number) => handleCurrentElement(e)}
                elementIndex={currentRowIndex}
            >
                <div className="analyse-page-details">
                    <div className="analyse-page-details-close">
                        <IconButton onClick={() => setIsDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div className="analyse-page-details-header">
                        <Avatar
                            variant="circular"
                            src={"#"}
                            sx={{
                                width: 80,
                                height: 80,
                                backgroundColor: variables["beneficiary-avatar-background"],
                                color: variables["beneficiary-avatar-letter"],
                                fontSize: 18,
                            }}
                            imgProps={{ sx: { objectFit: "cover" } }}
                        >
                            {getInitials(
                                `${analyseHistoryMemoized[currentRowIndex]?.firstname} ${analyseHistoryMemoized[currentRowIndex]?.lastname}`,
                            )}
                        </Avatar>
                        <div className="analyse-page-details-header-title">
                            <div>{`${analyseHistoryMemoized[currentRowIndex]?.firstname} ${analyseHistoryMemoized[currentRowIndex]?.lastname}`}</div>
                            {firstDeclaration?.quarter != Infinity && firstDeclaration?.year != Infinity && (
                                <div className="analyse-page-details-header-subtitle">
                                    {t("Analyse.FirstDeclaration", {
                                        quarter: firstDeclaration?.quarter,
                                        year: firstDeclaration?.year,
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="analyse-page-details-table">
                        {/* <H3 color="grey">{t("Analyse.DeclaredRevenue")}</H3> */}
                        <h3>{t("Analyse.DeclarationHistory")}</h3>
                        <LastElementTable
                            columns={detailsColumns}
                            data={analyseHistoryDetails ?? []}
                            max={9999}
                            bottomFooterContent={
                                <div className="analyse-page-details-table-footer">
                                    <div className="analyse-page-details-table-footer-text">
                                        <span className="red">
                                            {analyseHistoryDetails?.length} {t("Analyse.Periods")}
                                        </span>
                                        <span className="red">
                                            {formatNumber(detailsTotalGrossDuty / 100, {
                                                always2Digit: true,
                                                isCurrency: true,
                                            })}
                                        </span>
                                    </div>
                                </div>
                            }
                            maxHeight={550}
                        />
                    </div>
                </div>
            </ElementDetails>
            <div className="analyse-page-header">
                <h1>{t("Analyse.AnalyseHistory")}</h1>
                <div className="analyse-page-header-subtitle">{t("Analyse.Subtitle")}</div>
            </div>
            <div className="separator"></div>
            {analyseHistoryStatus == "pending" ? (
                <div style={{ textAlign: "center" }}>
                    <Spinner />
                </div>
            ) : (
                <div className="analyse-page-main">
                    <BlockCards data={cards} borderLeft compact />
                    <div className="analyse-page-main-data">
                        <h2>{t("Analyse.DeclaredRevenue")}</h2>
                        <div className="analyse-page-filters">
                            <StyledTextField
                                value={query}
                                onChange={(e: any) => handleQuery(e)}
                                placeholder={t("Management.Search")}
                                fullWidth
                                InputProps={{
                                    endAdornment: <SearchIcon sx={{ color: "#A8A8A8" }} />,
                                }}
                            />
                            {t("Analyse.From")}
                            <StyledSelect
                                // value={`{"startQuarter":${contributionList[1]?.quarter},"startYear":${contributionList[1]?.year}}`}
                                value={JSON.stringify({
                                    startQuarter: dateFilters.startQuarter,
                                    startYear: dateFilters.startYear,
                                })}
                                onChange={(e: any) =>
                                    setDateFilters((curr) => ({
                                        ...curr,
                                        ...JSON.parse(e.target.value),
                                    }))
                                }
                            >
                                {contributionList &&
                                    contributionList
                                        .filter((item) => item.quarter != 0)
                                        .sort((a: any, b: any) => +new Date(a.startDate) - +new Date(b.startDate))
                                        .map((item, index) => (
                                            <StyledMenuItem
                                                value={JSON.stringify({
                                                    startQuarter: item.quarter,
                                                    startYear: item.year,
                                                })}
                                                key={index}
                                            >
                                                T{item.quarter} {item.year}
                                            </StyledMenuItem>
                                        ))}
                            </StyledSelect>
                            {t("Analyse.To")}
                            <StyledSelect
                                // value={`{"endQuarter":${contributionList.at(-1)?.quarter},"endYear":${contributionList.at(-1)?.year}}`}
                                value={JSON.stringify({
                                    endQuarter: dateFilters.endQuarter,
                                    endYear: dateFilters.endYear,
                                })}
                                onChange={(e: any) =>
                                    setDateFilters((curr) => ({
                                        ...curr,
                                        ...JSON.parse(e.target.value),
                                    }))
                                }
                            >
                                {contributionList &&
                                    contributionList
                                        .filter((item) => item.quarter != 0)
                                        .sort((a: any, b: any) => +new Date(a.startDate) - +new Date(b.startDate))
                                        .map((item, index) => (
                                            <StyledMenuItem
                                                value={JSON.stringify({
                                                    endQuarter: item.quarter,
                                                    endYear: item.year,
                                                })}
                                                key={index}
                                            >
                                                T{item.quarter} {item.year}
                                            </StyledMenuItem>
                                        ))}
                            </StyledSelect>
                        </div>
                        <div className="analyse-page-table">
                            <LastElementTable
                                columns={columns}
                                data={analyseHistoryMemoized ?? []}
                                max={9999}
                                bottomFooterContent={<></>}
                                maxHeight={550}
                                onScrollEnd={() => loadMoreHistory()}
                                onRowClickId="unique_identifier"
                                onRowClick={handleOpenDrawer}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalysePage;
